/**
 * +grid: the Waystone Grid screen's view-model (pure, DOM-free, unit-tested; docs/design/waystone-grid.md
 * "UI"). Node states for drawing, the planning path (shortest route from what is allocated to a
 * clicked star), search, and tooltip lines. The screen (grid.ts) only draws and sends intents.
 */
import type { ClassId } from '@keystone/sim';
import {
  GRID_THEME_NAME,
  type GridNode,
  getGrid,
  gridGateOf,
  gridNodeText,
  slotSelectorText,
} from '@keystone/content';
import {
  type GridKeys,
  type GridPoints,
  type Profile,
  GRID_RESPEC_GOLD,
  GRID_RESPEC_LEVEL,
  gridKeys,
  gridPoints,
  gridReach,
  gridRefundBlocker,
  gridRefundCost,
  gridStateOf,
} from '@keystone/progression';

export type NodeState =
  | 'own-gate' // the hero's start (always "allocated")
  | 'allocated'
  | 'planned' // in the planning preview
  | 'available' // touches the allocation (or the plan): 1 point
  | 'unreached' // further away
  | 'sealed' // a lock not yet reachable
  | 'openable' // a lock touching the allocation, a key in hand
  | 'needs-key' // a lock touching the allocation, no key
  | 'open' // an opened lock
  | 'dormant';

export interface GridView {
  classId: ClassId;
  gate: string;
  level: number;
  allocated: ReadonlySet<string>;
  locks: ReadonlySet<string>;
  /** Allocated stars + opened locks connected to the gate (and the gate). */
  reach: ReadonlySet<string>;
  points: GridPoints;
  keys: GridKeys;
  gold: number;
}

export function gridView(p: Profile): GridView | null {
  const c = p.character;
  const ch = c ? p.characters[c.classId] : undefined;
  const gate = c ? gridGateOf(c.classId) : undefined;
  if (!c || !ch || !gate) return null;
  const st = gridStateOf(ch);
  return {
    classId: c.classId,
    gate,
    level: ch.hero.level,
    allocated: new Set(st.nodes),
    locks: new Set(st.locks),
    reach: gridReach(c.classId, st),
    points: gridPoints(p, c.classId),
    keys: gridKeys(p, c.classId),
    gold: p.currencies.gold ?? 0,
  };
}

const ALLOCATABLE = new Set(['gate', 'minor', 'slot', 'notable', 'utility', 'keystone']);

/** A node's state for drawing, given the planning preview. */
export function nodeState(v: GridView, n: GridNode, plan: readonly string[] = []): NodeState {
  if (n.id === v.gate) return 'own-gate';
  if (n.type === 'dormant') return 'dormant';
  if (n.type === 'lock') {
    if (v.locks.has(n.id)) return 'open';
    if (!n.links.some((l) => v.reach.has(l) || plan.includes(l))) return 'sealed';
    return v.keys.held > 0 ? 'openable' : 'needs-key';
  }
  if (v.allocated.has(n.id)) return 'allocated';
  if (plan.includes(n.id)) return 'planned';
  if (n.links.some((l) => v.reach.has(l) || plan.includes(l) || (v.locks.has(l) && reachedThroughLock(v, l, plan)))) return 'available';
  return 'unreached';
}

/** An opened lock counts as reached when it touches the reach or the plan. */
function reachedThroughLock(v: GridView, lockId: string, plan: readonly string[]): boolean {
  const g = getGrid();
  return (g.byId.get(lockId)?.links ?? []).some((l) => v.reach.has(l) || plan.includes(l));
}

/**
 * Shortest route (fewest new stars) from the allocation + plan to `target`, through allocatable
 * stars and opened locks only; the stars to add, in order (null = unreachable / not allocatable).
 */
export function planPathTo(v: GridView, plan: readonly string[], target: string): string[] | null {
  const g = getGrid();
  const t = g.byId.get(target);
  if (!t || !ALLOCATABLE.has(t.type) || t.id === v.gate || v.allocated.has(target) || plan.includes(target)) return null;
  const have = new Set<string>([...v.reach, ...plan]);
  // Opened locks next to anything we have are free stepping stones.
  const parent = new Map<string, string | null>();
  const q: string[] = [];
  for (const id of [...have].sort()) {
    parent.set(id, null);
    q.push(id);
  }
  while (q.length) {
    const id = q.shift()!;
    if (id === target) break;
    for (const l of g.byId.get(id)?.links ?? []) {
      if (parent.has(l)) continue;
      const n = g.byId.get(l);
      if (!n) continue;
      const passable = (n.type === 'lock' && v.locks.has(l)) || (ALLOCATABLE.has(n.type) && n.id !== v.gate);
      if (!passable) continue;
      parent.set(l, id);
      q.push(l);
    }
  }
  if (!parent.has(target)) return null;
  const path: string[] = [];
  for (let at: string | null = target; at && !have.has(at); at = parent.get(at) ?? null) {
    if (g.byId.get(at)?.type !== 'lock') path.unshift(at);
  }
  return path;
}

/** Clicking a planned star drops it and everything planned after it. */
export function truncatePlan(plan: readonly string[], id: string): string[] {
  const i = plan.indexOf(id);
  return i < 0 ? [...plan] : plan.slice(0, i);
}

/** Stars whose name or rule text contains every word of `query` (case-insensitive). */
export function searchGrid(query: string): string[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const out: string[] = [];
  for (const n of getGrid().nodes) {
    const hay = `${n.name} ${gridNodeText(n).join(' ')} ${n.type} ${GRID_THEME_NAME[n.theme]}`.toLowerCase();
    if (words.every((w) => hay.includes(w))) out.push(n.id);
  }
  return out;
}

const TYPE_LABEL: Readonly<Record<GridNode['type'], string>> = {
  gate: 'Constellation gate',
  minor: 'Star',
  slot: 'Ability star',
  notable: 'Notable',
  utility: 'Wayfarer star',
  lock: 'Sealed sigil',
  keystone: 'Keystone',
  dormant: 'Unformed star',
};

export interface Tooltip {
  title: string;
  kind: string;
  lines: string[];
  /** Grey footnote (state, cost, how to act). */
  note: string;
  flavour?: string;
}

/** Tooltip content for a star in the current view (and plan). */
export function tooltipFor(v: GridView, n: GridNode, plan: readonly string[] = []): Tooltip {
  const st = nodeState(v, n, plan);
  const kind = `${TYPE_LABEL[n.type]}${n.region ? ` · ${cap(n.region)}` : ''} · ${GRID_THEME_NAME[n.theme]}`;
  let lines = gridNodeText(n);
  if (n.type === 'gate') lines = [n.gate === v.classId ? 'Your start: free, always yours.' : `The ${cap(n.gate ?? '')}'s start. Another class may walk through it.`];
  if (n.type === 'lock') lines = ['A sealed sigil guards the stars beyond it.', 'One Grid Key opens it for good (no point spent).'];
  if (n.type === 'dormant') lines = [n.flavour ?? 'A star that has not formed yet.'];
  if (n.type === 'slot') {
    const e = n.effects[0];
    if (e?.kind === 'slot') lines.push(`Follows whatever sits on that slot (${slotSelectorText(e.sel)}), even after the Tower changes it.`);
  }
  let note = '';
  switch (st) {
    case 'own-gate':
      note = 'Your gate';
      break;
    case 'allocated': {
      const why = gridRefundBlocker(v.classId, { nodes: [...v.allocated], locks: [...v.locks] }, n.id);
      const cost = gridRefundCost(v.level, 1);
      note = why ? `Allocated · ${why}` : `Allocated · right-click to refund${cost ? ` (${cost} gold)` : ' (free)'}`;
      break;
    }
    case 'planned':
      note = 'Planned · click to drop it (and what follows)';
      break;
    case 'available':
      note = 'Click to plan (1 point)';
      break;
    case 'unreached': {
      const path = planPathTo(v, plan, n.id);
      note = path ? `Click to plan a route: ${path.length} points` : 'Beyond a sealed sigil';
      break;
    }
    case 'openable':
      note = `Click to open with a Grid Key (${v.keys.held} held)`;
      break;
    case 'needs-key':
      note = 'You need a Grid Key: Barrow, rare beasts, secret caches, quests, the Tower';
      break;
    case 'sealed':
      note = 'Reach it first';
      break;
    case 'open':
      note = 'Opened';
      break;
    case 'dormant':
      note = 'Forms in a later age (level cap 30)';
      break;
  }
  return { title: n.name, kind, lines, note, ...(n.flavour && n.type !== 'dormant' ? { flavour: n.flavour } : {}) };
}

/** Planning summary: cost against free points. */
export function planSummary(v: GridView, plan: readonly string[]): { cost: number; free: number; ok: boolean; text: string } {
  const cost = plan.length;
  const free = v.points.free;
  const ok = cost > 0 && cost <= free;
  return { cost, free, ok, text: cost ? `Plan: ${cost} point${cost === 1 ? '' : 's'} of ${free} free` : '' };
}

/** Respec price for the current allocation (free below level 10). */
export function respecCost(v: GridView): number {
  return v.level >= GRID_RESPEC_LEVEL ? GRID_RESPEC_GOLD * v.allocated.size : 0;
}

const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);
